import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { TableTools } from '../../components/ui/TableTools';
import { num } from '../../format';
import { useAuth } from '../auth/AuthContext';
import { useUnitName } from '../components/useUnitName';
import { downloadBlob } from '../labels/api';
import { useNavigation } from '../layout/navigation';
import { listProjects } from '../projects/api';
import { createDrafts, type WantedLine } from '../purchases/api';
import { shortages, shortagesXlsx, type ShortageRow } from './api';

/** How a supplier is keyed while grouping. Null is a real answer — "nobody has
 *  sold us this yet" — and it has to survive being used as a map key. */
const NONE = '\u0000none';

/**
 * What the running projects need and the shelf has not got.
 *
 * Distinct from "running out": a minimum level is a standing opinion about a
 * part, while this is arithmetic over what is actually planned. A part can be
 * comfortably above its minimum and still be short by two hundred because a
 * batch of boards was planned last week.
 *
 * The supplier is the last one this part was bought from, which is the answer to
 * the only question the list raises: order it from whom? Picking rows and
 * pressing the button raises one draft order per supplier, because an order
 * naming parts from four shops cannot be placed anywhere.
 */
export function ShortagePanel() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { open } = useNavigation();
  const unitName = useUnitName();

  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<{ code: string; id: string }[]>([]);

  const rows = useQuery({ queryKey: ['projects', 'shortages'], queryFn: shortages });
  // The report names projects by code, which is what a person reads; opening one
  // needs its identifier, and the list is the only place that holds both.
  const projects = useQuery({ queryKey: ['projects', 'list', ''], queryFn: () => listProjects() });

  const canBuy = can('purchase.write');

  // Memoised, not just defaulted: a fresh empty array on every render would
  // re-run everything derived from it on every render.
  const list = useMemo(() => rows.data ?? [], [rows.data]);

  const toggle = (id: string) =>
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selected = useMemo(
    () => list.filter((row) => chosen.has(row.component_id)),
    [list, chosen],
  );

  // How many orders the button is about to raise, so pressing it holds no
  // surprises.
  const vendorCount = useMemo(
    () => new Set(selected.map((row) => row.vendor_id ?? NONE)).size,
    [selected],
  );

  const raise = useMutation({
    mutationFn: () => {
      const lines: WantedLine[] = selected.map((row) => ({
        component_id: row.component_id,
        quantity: row.shortage,
        vendor_id: row.vendor_id,
        // Six months later "for J-4KQ8ZM and J-9TT1PA" is the only thing that
        // explains why forty of something were bought.
        purpose_note: row.projects.join(', ') || null,
      }));
      return createDrafts(lines, t('stock.shortage.note'));
    },
    onSuccess: (purchases) => {
      setMade(purchases.map((row) => ({ code: row.code, id: row.id })));
      setChosen(new Set());
      setError(null);
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const line = (row: ShortageRow) => (
    <tr key={row.component_id} className={chosen.has(row.component_id) ? 'is-chosen' : undefined}>
      {canBuy && (
        <td>
          <input
            type="checkbox"
            checked={chosen.has(row.component_id)}
            onChange={() => toggle(row.component_id)}
            aria-label={row.name}
          />
        </td>
      )}
      <td>
        <button type="button" className="link" onClick={() => open('component', row.component_id)}>
          {row.name}
        </button>
        <span className="components__code"> {row.internal_code}</span>
      </td>
      <td>{num(row.required)}</td>
      <td>{num(row.on_hand)}</td>
      <td>{num(row.ordered)}</td>
      <td>{Number(row.draft_quantity) > 0 ? num(row.draft_quantity) : '—'}</td>
      <td>
        <strong className="stock__negative">{num(row.shortage)}</strong>
        <em className="stock__when"> {unitName(row.unit_code)}</em>
      </td>
      <td>{row.vendor_name ?? <em className="hint">{t('stock.shortage.noVendor')}</em>}</td>
      <td>
        {/* The codes are the argument for the row — "who is asking for forty of
            these" — so each one leads to the project that is asking. */}
        {row.projects.map((code) => (
          <button
            key={code}
            type="button"
            className="badge badge--link"
            onClick={(event) => {
              event.stopPropagation();
              const found = projects.data?.find((project) => project.code === code);
              if (found) open('project', found.id);
            }}
          >
            {code}
          </button>
        ))}
      </td>
    </tr>
  );

  return (
    <section className="shortage">
      <div className="panel__section-head">
        <h3>{t('stock.tab.shortage')}</h3>
        <TableTools
          label={t('stock.tab.shortage')}
          onExport={() =>
            void shortagesXlsx().then((blob) => downloadBlob(blob, 'shortages.xlsx'))
          }
        />
      </div>

      {rows.isLoading && <p className="panel__hint">{t('common.loading')}</p>}
      {rows.data?.length === 0 && <p className="panel__hint">{t('stock.shortage.empty')}</p>}

      {made.length > 0 && (
        <p className="stock__ok">
          {t('stock.shortage.created', { n: made.length })}{' '}
          {made.map((row) => (
            <button
              key={row.id}
              type="button"
              className="link"
              onClick={() => open('purchase', row.id)}
            >
              {row.code}
            </button>
          ))}
        </p>
      )}
      {error && <p className="panel__error">{t(`stock.shortage.errors.${error}`, error)}</p>}

      {list.length > 0 && (
        <>
          <div className="scroll-x scroll-y">
            <table className="shortage__grid">
              <thead>
                <tr>
                  {canBuy && <th aria-label={t('stock.shortage.choose')} />}
                  <th>{t('stock.table.column.name')}</th>
                  <th>{t('stock.shortage.required')}</th>
                  <th>{t('stock.shortage.onHand')}</th>
                  <th>{t('stock.shortage.ordered')}</th>
                  <th>{t('stock.shortage.drafts')}</th>
                  <th>{t('stock.shortage.missing')}</th>
                  <th>{t('stock.shortage.vendor')}</th>
                  <th>{t('stock.shortage.projects')}</th>
                </tr>
              </thead>
              <tbody>{list.map(line)}</tbody>
            </table>
          </div>

          {canBuy && (
            <div className="shortage__actions">
              <button
                type="button"
                className="btn btn--primary"
                disabled={selected.length === 0 || raise.isPending}
                onClick={() => raise.mutate()}
              >
                {t('stock.shortage.raise', { rows: selected.length, orders: vendorCount })}
              </button>
              <button
                type="button"
                className="btn"
                disabled={list.length === 0}
                onClick={() => setChosen(new Set(list.map((row) => row.component_id)))}
              >
                {t('stock.shortage.chooseAll')}
              </button>
              {/* Said out loud, because an order with no prices in it looks
                  half-finished unless you know it is meant to be. */}
              <span className="hint">{t('stock.shortage.priceHint')}</span>
            </div>
          )}
        </>
      )}
    </section>
  );
}
